import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useComprehensiveTopNav from 'hooks/useComprehensiveTopNav';
import { Image, SearchBar, Typography, LoadingIndicator, Pagination, DropdownButton } from 'lib/components';
import { useWindowResize } from 'hooks/useWindowResize';
import { AdminsDataTable } from 'lib/components/DataTables';
import {
	useAppDispatch,
	actions,
	useSelectAdmins,
	useSelectRequest,
	useSelectCurrentUser,
	RequestManager,
} from 'store';
import colors from 'res/colors';
import routes from 'lib/api/routes';
import { useCustomToast, usePagination, useSearch } from 'hooks';
import { DropdownItem } from 'lib/components/types';
import { Roles } from 'lib/util';
import classes from './index.module.scss';

/**
 * Dropdown options for filtering admins by role
 * Used in the role filter dropdown component
 */
const ROLE_DROPDOWN_ITEMS: DropdownItem<number>[] = [
	{ label: 'Super Admin', value: Roles.SUPER_ADMIN },
	{ label: 'Admin', value: Roles.ADMIN },
];

/**
 * Admin management screen component
 * Displays a list of administrators with filtering, search, and management capabilities
 * Allows creating, editing, suspending, and deleting admin users
 */
const AdminsScreen = () => {
	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Navigation helper
	const { push } = useHistory();
	// Responsive design helpers
	const { isSmallDevice, isSmallerDevice } = useWindowResize();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Admin users data from Redux store
	const admins = useSelectAdmins();
	// Current logged-in user information
	const me = useSelectCurrentUser();
	// Active API request tracking
	const { active } = useSelectRequest();

	// ID of the currently selected admin (for actions like delete/suspend)
	const [checked, setChecked] = useState<number[]>([]);
	// Search query for filtering admins
	const [query, setQuery] = useState('');
	// Selected role filter value (-1 means no filter)
	const [selectedRoleDropdownItem, setSelectedRoleDropdownItem] = useState<number>(-1);

	/**
	 * Fetch admin users data on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchAdmins());
	}, []);

	/**
	 * Set up pagination for admin users list
	 * Returns data and handlers for paginated display
	 */
	const { isDataFetched, listing, setData, paginatedData, paginationProps } = usePagination(admins, {});

	/**
	 * Handle search functionality using custom hook
	 * Searches across admin name, email, and phone number
	 */
	useSearch(
		query,
		// Reset function - called when query is empty
		() => {
			setData(listing);
		},
		// Search function - called when query has content
		() => {
			const result = listing.filter(
				(admin) =>
					admin.name.toLowerCase().search(query.toLowerCase()) !== -1 ||
					admin.email.toLowerCase().search(query.toLowerCase()) !== -1 ||
					(admin.phoneNumber && admin.phoneNumber.toLowerCase().search(query.toLowerCase()) !== -1)
			);
			setData(result);
		}
	);

	/**
	 * Monitor API requests for admin deletion and suspension actions
	 * Shows toast notifications for success/failure and resets selection
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		// Handle admin deletion request
		if (RM.req(routes.ADMIN_ID(checked[0]), 'DELETE')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				setChecked([]);
			}
		}

		// Handle admin suspension toggle request
		if (RM.req(routes.ADMIN_SUSPEND(checked[0]), 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				setChecked([]);
			}
		}
	}, [active]);

	/**
	 * Filter admins by selected role when role filter changes
	 * Only applies when a role is selected from the dropdown
	 */
	useEffect(() => {
		if (selectedRoleDropdownItem === -1) return;

		const result = listing.filter((admin) => admin.roleId === selectedRoleDropdownItem);
		setData(result);
	}, [selectedRoleDropdownItem]);

	/**
	 * Handle checkbox selection for admin rows
	 * Only one admin can be selected at a time for actions
	 *
	 * @param id - ID of the admin being checked/unchecked
	 * @param isChecked - Whether the checkbox is being checked or unchecked
	 */
	const handleCheckAction = (id: number, isChecked: boolean) => {
		if (isChecked && !checked.includes(id)) {
			setChecked([id]);
			return;
		}

		if (!isChecked || checked.includes(id)) {
			setChecked([]);
		}
	};

	/**
	 * Handle admin deletion action
	 * Dispatches delete action for the selected admin
	 */
	const handleDelete = () => {
		dispatch(actions.deleteAdmin(checked[0]));
	};

	/**
	 * Handle admin suspension toggle action
	 * Dispatches suspend toggle action for the selected admin
	 */
	const handleSuspend = () => {
		dispatch(actions.toggleAdminSuspend(checked[0]));
	};

	/**
	 * Handle role filter dropdown selection
	 *
	 * @param value - Selected role ID from the dropdown
	 */
	const handleRoleDropdownItemSelect = (value: number) => {
		setSelectedRoleDropdownItem(value);
	};

	// Set up the top navigation bar with title
	useComprehensiveTopNav('Admins');

	// Show loading indicator while data is being fetched
	if (!isDataFetched) return <LoadingIndicator />;

	return (
		<>
			<div className={classes['inventories']}>
				<div
					className={classes['row']}
					style={{
						justifyContent: 'space-between',
					}}
				>
					<div
						style={{
							flex: 1,
							marginRight: isSmallerDevice ? 0 : '2rem',
						}}
					>
						<SearchBar
							query={query}
							setQuery={setQuery}
							onSearch={() => console.log({ query })}
							className={classes['inventories-searchbar']}
							placeholder="Search..."
							searchIconColor="primary"
						/>
					</div>
					<div
						className={classes['row']}
						style={{
							marginTop: '1.3rem',
						}}
					>
						<button
							className={`${classes['row']} ${classes['add-new']}`}
							onClick={() => push('/admins/create')}
						>
							<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Add new</Typography>
							<div>
								<Image src={'icPlusPrimary'} />
							</div>
						</button>
					</div>
				</div>
				<div
					className={classes['row']}
					style={{
						marginTop: '1.2rem',
						alignItems: 'center',
					}}
				>
					<div
						style={{
							marginRight: '1rem',
						}}
					>
						<Typography weight="medium" variant={isSmallDevice ? 'h5' : 'h6'}>
							Filter by:
						</Typography>
					</div>
					<div
						style={{
							marginRight: '1rem',
						}}
					>
						<DropdownButton
							title="Role"
							list={ROLE_DROPDOWN_ITEMS}
							onItemSelect={handleRoleDropdownItemSelect}
						/>
					</div>
				</div>
				<div className={classes['row']} style={{ justifyContent: 'space-between' }}>
					<div className={classes['row']} style={{ marginTop: isSmallerDevice ? 0 : '0.5rem' }}>
						{checked.length !== 0 && checked[0] !== me.id && (
							<>
								<button
									className={classes['button']}
									style={{
										backgroundColor: colors.secondaryText,
										borderColor: colors.secondaryText,
										marginRight: '1rem',
									}}
									onClick={handleSuspend}
								>
									<Image
										src="icLock"
										style={{
											marginRight: '0.5rem',
										}}
									/>
									<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Toggle Suspend</Typography>
								</button>
								<button
									className={classes['button']}
									style={{
										backgroundColor: '#FCD3D1',
										borderColor: '#FF3B30',
									}}
									onClick={handleDelete}
								>
									<Image
										src="icTrashDanger"
										style={{
											marginRight: '0.5rem',
										}}
									/>
									<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="danger">
										Delete
									</Typography>
								</button>
							</>
						)}
					</div>
				</div>
				<AdminsDataTable admins={paginatedData} checkedId={checked[0] ?? -1} checkAction={handleCheckAction} />
				<Pagination {...paginationProps} />
			</div>
		</>
	);
};

export default AdminsScreen;
